import { useState, useEffect, useCallback, useRef } from "react";
import {
  FileText,
  BarChart3,
  Users,
  CreditCard,
  Activity,
  Trophy,
  Tag,
  HandCoins,
  BookOpen,
  GraduationCap,
  Calendar,
  Mail,
  Wand2,
  Upload,
  FlaskConical,
  FileCheck,
  MessageSquare,
  CloudDownload,
  ListChecks,
  Plug,
  ScrollText,
  Contact,
  Megaphone,
  TrendingDown,
  Lightbulb,
  ThumbsDown,
  Flag,
  KeyRound,
  Inbox,
  Ticket,
  DollarSign,
} from "lucide-react";

export const VISIT_COUNTS_KEY = "edufocal_admin_visit_counts";
export const FAVOURITES_KEY = "edufocal_admin_favourites";

export interface AdminItem {
  title: string;
  description: string;
  icon: React.ElementType;
  href: string;
}

export const ALL_ADMIN_ITEMS: AdminItem[] = [
  { title: "Manage Questions", description: "Create, edit, and manage all questions in the platform.", icon: FileText, href: "/admin/questions" },
  { title: "Question Analytics", description: "View question performance, difficulty, and attempt data.", icon: BarChart3, href: "/admin/question-analytics" },
  { title: "Auto Generation", description: "Generate curriculum-aligned questions using AI.", icon: FlaskConical, href: "/admin/question-generation" },
  { title: "AI Import", description: "Import questions from PDFs, Word docs, and images.", icon: Upload, href: "/admin/ai-import" },
  { title: "Quizzative Questions", description: "Browse and manage synced Quizzative questions.", icon: ListChecks, href: "/admin/quizzative-questions" },
  { title: "Question Bank Export", description: "Sync questions from the Quizzative export API.", icon: CloudDownload, href: "/admin/quizzative-export" },
  { title: "Integration Hub", description: "Configure and monitor Quizzative integration settings.", icon: Plug, href: "/admin/quizzative-integration" },
  { title: "API Logs", description: "View logs from Quizzative API calls and syncs.", icon: ScrollText, href: "/admin/quizzative-api-logs" },
  { title: "Bulk Export", description: "Export questions in bulk from Quizzative.", icon: Upload, href: "/admin/quizzative-bulk-export" },
  { title: "Question Sandbox", description: "Review and approve questions before publishing.", icon: FileCheck, href: "/admin/sandbox" },
  { title: "Platform Stats", description: "Overview of platform usage, signups, and engagement.", icon: BarChart3, href: "/admin/stats" },
  { title: "All Users", description: "Browse and manage all user accounts.", icon: Users, href: "/admin/users" },
  { title: "Subscriptions", description: "View active, cancelled, and failed subscriptions.", icon: CreditCard, href: "/admin/subscriptions" },
  { title: "Revenue", description: "Cash forecast by week, MRR, churn, retention and LTV.", icon: DollarSign, href: "/admin/revenue" },
  { title: "Metrics", description: "MRR, churn, LTV, movements, forecast, recovery and cancellations, the Baremetrics view.", icon: BarChart3, href: "/admin/metrics" },
  { title: "Payment Analytics", description: "Payment success rates, recovery tracking, and revenue.", icon: DollarSign, href: "/admin/payment-analytics" },
  { title: "Stripe Pricing", description: "Manage Stripe pricing plans and configurations.", icon: CreditCard, href: "/admin/stripe-pricing" },
  { title: "Parents", description: "View parent accounts and their linked children.", icon: Contact, href: "/admin/parents" },
  { title: "Activity Stream", description: "Real-time user activity feed for monitoring.", icon: Activity, href: "/admin/activity" },
  { title: "XP Decay Monitor", description: "Monitor XP decay rates and inactive student engagement.", icon: TrendingDown, href: "/admin/xp-decay" },
  { title: "PEP Predictions", description: "View AI-powered PEP exam readiness predictions.", icon: GraduationCap, href: "/admin/pep-predictions" },
  { title: "Prize Management", description: "Manage milestone prizes and draw campaigns.", icon: Trophy, href: "/admin/prize-management" },
  { title: "Discount Codes", description: "Create and manage promotional discount codes.", icon: Tag, href: "/admin/discounts" },
  { title: "Partner Vouchers", description: "Manage partner-generated voucher codes.", icon: Ticket, href: "/admin/vouchers" },
  { title: "Referral Payouts", description: "Track and process referral program payouts.", icon: HandCoins, href: "/admin/referrals" },
  { title: "Creator Applications", description: "Review and manage content creator applications.", icon: Users, href: "/admin/creator-applications" },
  { title: "Creator Videos", description: "Manage uploaded creator video content.", icon: Megaphone, href: "/admin/creator-videos" },
  { title: "Reading Materials", description: "Upload and manage structured reading lessons.", icon: BookOpen, href: "/admin/reading-materials" },
  { title: "Learning Materials", description: "Manage subject training documents and resources.", icon: Lightbulb, href: "/admin/materials" },
  { title: "Curriculum", description: "Manage grades, subjects, topics, and learning objectives.", icon: GraduationCap, href: "/admin/curriculum" },
  { title: "Academic Calendar", description: "Manage academic years, terms, breaks, and PEP windows.", icon: Calendar, href: "/admin/academic-calendar" },
  { title: "Brevo Mappings", description: "Configure email list mappings for Brevo integration.", icon: Mail, href: "/admin/brevo-mappings" },
  { title: "Knowledge Base", description: "Manage help articles and support documentation.", icon: BookOpen, href: "/admin/knowledge-base" },
  { title: "Announcements", description: "Create and manage platform announcements.", icon: Megaphone, href: "/admin/announcements" },
  { title: "Banners", description: "Create and manage platform announcement banners.", icon: Megaphone, href: "/admin/banners" },
  { title: "WhatsApp Manager", description: "Send WhatsApp messages and view message logs.", icon: MessageSquare, href: "/admin/whatsapp" },
  { title: "WhatsApp Inbox", description: "View and respond to incoming WhatsApp conversations.", icon: Inbox, href: "/admin/whatsapp-inbox" },
  { title: "Dummy Accounts", description: "Generate test accounts and run platform health checks.", icon: FlaskConical, href: "/admin/dummy-accounts" },
  { title: "Cancellation Feedback", description: "See why subscribers cancelled and analyse churn patterns.", icon: ThumbsDown, href: "/admin/cancellation-feedback" },
  { title: "User Feedback", description: "View suggestions and bug reports from users.", icon: MessageSquare, href: "/admin/feedback" },
  { title: "Reported Questions", description: "Review questions flagged by students as having errors or issues.", icon: Flag, href: "/admin/reported-questions" },
  { title: "API Keys", description: "Generate and manage API keys for external integrations and mobile app access.", icon: KeyRound, href: "/admin/api-keys" },
];

export const ITEM_BY_HREF = Object.fromEntries(ALL_ADMIN_ITEMS.map((item) => [item.href, item]));

async function syncFavouritesToServer(favourites: string[]) {
  try {
    await fetch('/api/user/ui-preferences', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ adminFavourites: favourites }),
    });
  } catch {}
}

export function useAdminQuickAccess() {
  const [visitCounts, setVisitCounts] = useState<Record<string, number>>({});
  const [favourites, setFavourites] = useState<string[]>([]);
  const serverSynced = useRef(false);

  // Load from localStorage immediately, then override with server values
  useEffect(() => {
    try {
      const raw = localStorage.getItem(VISIT_COUNTS_KEY);
      if (raw) setVisitCounts(JSON.parse(raw));
    } catch {}

    let localFavourites: string[] = [];
    try {
      const raw = localStorage.getItem(FAVOURITES_KEY);
      if (raw) {
        localFavourites = JSON.parse(raw);
        setFavourites(localFavourites);
      }
    } catch {}

    // Fetch from server to override local state and ensure cross-device sync

    fetch('/api/user/ui-preferences')
      .then(r => r.ok ? r.json() : null)
      .then(prefs => {
        if (prefs?.adminFavourites) {
          // Server has favourites: apply them (cross-device sync)
          setFavourites(prefs.adminFavourites);
          try { localStorage.setItem(FAVOURITES_KEY, JSON.stringify(prefs.adminFavourites)); } catch {}
        } else if (localFavourites.length > 0) {
          // Server has no favourites yet: upload current local ones
          syncFavouritesToServer(localFavourites);
        }
        serverSynced.current = true;
      })
      .catch(() => { serverSynced.current = true; });
  }, []);

  // Listen for same-tab sync between hook instances
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail) setFavourites(detail);
    };
    window.addEventListener('favourites-updated', handler);
    return () => window.removeEventListener('favourites-updated', handler);
  }, []);

  const toggleFavourite = useCallback((href: string) => {
    setFavourites((prev) => {
      const next = prev.includes(href) ? prev.filter((h) => h !== href) : [...prev, href];
      try { localStorage.setItem(FAVOURITES_KEY, JSON.stringify(next)); } catch {}
      syncFavouritesToServer(next);
      // Pass the new value directly so other instances don't need to read localStorage
      window.dispatchEvent(new CustomEvent('favourites-updated', { detail: next }));
      return next;
    });
  }, []);

  const frequentItems = Object.entries(visitCounts)
    .filter(([href]) => ITEM_BY_HREF[href] && !favourites.includes(href))
    .sort(([, a], [, b]) => b - a)
    .slice(0, 6)
    .map(([href]) => ITEM_BY_HREF[href]);

  const favouriteItems = favourites
    .filter((href) => ITEM_BY_HREF[href])
    .map((href) => ITEM_BY_HREF[href]);

  return { visitCounts, favourites, toggleFavourite, frequentItems, favouriteItems };
}
